// src/App.tsx
import "../Report.css";

import EmissionsBreakdownChart from "@/pages/communication/common/EmissionsBreakdownChart";

import ReportHeader from "@/components/ReportHeader";
import ReportFooter from "@/components/ReportFooter";
import { Flame, Car, FireExtinguisher, Zap, Building } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { companyAtom } from "@/store/company";
import { datesAtom } from "@/store/dates";
import { Category, FacilityEmission } from "@/types";
import { getApiUrl } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { categoriesAtom } from "@/store/categories";

interface IntroPageProps {
  title: string;
  page?: number;
}

export const SedesPage = ({ title, page }: IntroPageProps) => {
  const { token } = useAuth();

  const [company] = useAtom(companyAtom);
  const [dates] = useAtom(datesAtom);
  const [categories] = useAtom(categoriesAtom);

  const { data: categoriesData } = useQuery<any>({
    queryKey: ["categories", categories?.join(",")],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(`categories/?ids=${categories?.join(",")}`),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data types");
      }
      return response.json();
    },
    enabled: !!token && !!categories,
  });

  const { data } = useQuery<FacilityEmission[]>({
    queryKey: [
      "facilities-emissions",
      company?.id,
      dates?.fromDate,
      dates?.toDate,
    ],
    queryFn: async () => {
      const response = await fetch(
        getApiUrl(
          `companies/${company?.id}/facilities-emissions/?start_date=${dates?.fromDate}&end_date=${dates?.toDate}&categories=${categories?.join(",")}`,
        ),
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch data types");
      }
      return response.json();
    },
    enabled: !!token && !!company && !!dates,
  });

  const chunkArray = (array: any[], chunkSize: number) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const facilityChunks = data ? chunkArray(data, 4) : [];

  // If no facilities or only one page needed, render single page
  if (!data || data.length <= 4) {
    return (
      <section className="pdf-page">
        <ReportHeader title={title} />
        <div className="pdf-content text-lg grid grid-cols-2 gap-2">
          {data?.map((facility: any) => (
            <EmissionsBreakdownChart
              key={facility.id || facility.name}
              title={" "}
              icon={<Building className="h-6 w-6" />}
              titleDetails={
                <div className="flex items-center space-x-2">
                  <span className="ml-2 text-red-600">
                    {String(facility?.name)}
                  </span>
                  <span className="font-bold">
                    {String((facility?.emissions / 1000).toFixed(2))} ton CO2e
                  </span>
                </div>
              }
              data={[
                {
                  icon: <Flame className="h-6 w-6" />,
                  label: "Combustión",
                  value: Number(facility?.categories?.combustion),
                  type: "combustion",
                },
                {
                  icon: <Car className="h-6 w-6" />,
                  label: "Vehículos de empresa",
                  value: Number(facility?.categories?.consumption),
                  type: "vehicles",
                },
                {
                  icon: <FireExtinguisher className="h-6 w-6" />,
                  label: "Recargas",
                  value: Number(facility?.categories?.rechages),
                  type: "recharges",
                },
                {
                  icon: <Zap className="h-6 w-6" />,
                  label: "Electricidad",
                  value: Number(facility?.categories?.electricity),
                  type: "electricity",
                },
              ].filter(
                (item) =>
                  categoriesData?.results?.find(
                    (category: Category) =>
                      category.name.toLowerCase() === item.type.toLowerCase(),
                  )?.id > 0,
              )}
            />
          ))}
        </div>
        <ReportFooter page={page} />
      </section>
    );
  }

  // Multiple pages needed
  return (
    <>
      {facilityChunks.map((chunk, chunkIndex) => (
        <section key={chunkIndex} className="pdf-page">
          <ReportHeader title={title} />
          <div className="pdf-content text-lg grid grid-cols-2 gap-2">
            {chunk.map((facility: any) => (
              <EmissionsBreakdownChart
                key={facility.id || facility.name}
                title={" "}
                icon={<Building className="h-6 w-6" />}
                titleDetails={
                  <div className="flex items-center space-x-2">
                    <span className="ml-2 text-red-600">
                      {String(facility?.name)}
                    </span>
                    <span className="font-bold">
                      {String((facility?.emissions / 1000).toFixed(2))} ton CO2e
                    </span>
                  </div>
                }
                data={[
                  {
                    icon: <Flame className="h-6 w-6" />,
                    label: "Combustión",
                    value: Number(facility?.categories?.combustion),
                    type: "combustion",
                  },
                  {
                    icon: <Car className="h-6 w-6" />,
                    label: "Vehículos de empresa",
                    value: Number(facility?.categories?.consumption),
                    type: "vehicles",
                  },
                  {
                    icon: <FireExtinguisher className="h-6 w-6" />,
                    label: "Recargas",
                    value: Number(facility?.categories?.rechages),
                    type: "recharges",
                  },
                  {
                    icon: <Zap className="h-6 w-6" />,
                    label: "Electricidad",
                    value: Number(facility?.categories?.electricity),
                    type: "electricity",
                  },
                ].filter(
                  (item) =>
                    categoriesData?.results?.find(
                      (category: Category) =>
                        category.name.toLowerCase() === item.type.toLowerCase(),
                    )?.id > 0,
                )}
              />
            ))}
          </div>
          <ReportFooter page={page ? page + chunkIndex : chunkIndex + 1} />
        </section>
      ))}
    </>
  );
};
